/**
 * Version history dropdown for the editor header per TZ-02 §C2 /
 * CP-PAGE-03. Restore is append-only server-side (the old document
 * comes back as a NEW version), so the only destructive-feeling part —
 * replacing the current working copy — gets a confirm().
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../lib/api";
import {
  listTemplateHistory,
  restoreTemplateVersion,
  type TemplateVersionSummary,
} from "../lib/templates";
import { Button } from "./ui/button";

function formatStamp(raw: string | null): string {
  if (raw === null) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

export function TemplateHistoryPanel({
  templateId,
  onRestored,
}: {
  templateId: string;
  /** Called with the restored document — the page reseeds the editor. */
  onRestored: (restored: Record<string, unknown>) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const queryClient = useQueryClient();

  const history = useQuery({
    queryKey: ["template", templateId, "history"],
    queryFn: () => listTemplateHistory(templateId),
    enabled: open,
  });

  const restore = useMutation({
    mutationFn: (versionId: string) => restoreTemplateVersion(templateId, versionId),
    onSuccess: (restored) => {
      void queryClient.invalidateQueries({ queryKey: ["template", templateId] });
      setOpen(false);
      onRestored(restored);
    },
    onError: (err) => {
      setError(
        err instanceof ApiError
          ? err.problem.detail || err.problem.title
          : "Couldn't restore this version.",
      );
    },
  });

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent): void {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    window.addEventListener("mousedown", onPointerDown);
    return () => window.removeEventListener("mousedown", onPointerDown);
  }, [open]);

  function onRestoreClick(version: TemplateVersionSummary): void {
    setError(null);
    if (
      window.confirm(
        `Restore version ${version.version_number}? Your current state stays in history as its own version.`,
      )
    ) {
      restore.mutate(version.version_id);
    }
  }

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        History
      </Button>

      {open && (
        <div
          role="dialog"
          aria-label="Version history"
          className="absolute right-0 top-full z-40 mt-1 w-80 rounded-lg border border-neutral-200 bg-white p-2 shadow-lg"
        >
          {history.isLoading && (
            <p className="px-2 py-3 text-sm text-neutral-500">Loading history…</p>
          )}
          {history.error !== null && !history.isLoading && (
            <p className="px-2 py-3 text-sm text-neutral-500">
              Couldn&apos;t load history.
            </p>
          )}
          {error !== null && (
            <p role="alert" className="px-2 py-1 text-xs text-error-700">
              {error}
            </p>
          )}
          <ul className="max-h-80 divide-y divide-neutral-100 overflow-auto">
            {(history.data ?? []).map((version, index) => (
              <li
                key={version.version_id}
                className="flex items-center justify-between gap-2 px-2 py-2"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-neutral-900">
                    v{version.version_number}
                    {index === 0 && (
                      <span className="ml-1 text-xs font-normal text-neutral-500">
                        (current)
                      </span>
                    )}
                  </p>
                  <p className="truncate text-xs text-neutral-500">
                    {version.change_summary ?? "—"}
                    {version.created_at !== null &&
                      ` · ${formatStamp(version.created_at)}`}
                  </p>
                </div>
                {index !== 0 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    loading={restore.isPending && restore.variables === version.version_id}
                    onClick={() => onRestoreClick(version)}
                  >
                    Restore
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
